import type { ReactNode } from 'react'
import { Link } from 'react-router'

import { RecordLink, type PermissionId } from '@/shared/access'
import { cn } from '@/shared/ui'
import { inlineLink, targetSize } from '@/shared/ui/cn'

/**
 * One linked item in a field: `path` is a page in this app, `href` anything else; neither is plain
 * text. With `permission`, `path` links only for a caller who holds it, such as another record's
 * read permission, and with `scope` only at that scope (or one of several).
 */
export interface FieldLink {
  readonly label: string
  readonly path?: string
  readonly href?: string
  readonly permission?: PermissionId
  readonly scope?: string | readonly string[] | undefined
}

/** A details page's list of facts; holds `FieldRow`s. */
export function FieldList(props: { readonly children: ReactNode; readonly className?: string }) {
  return <dl className={cn('flex flex-col', props.className)}>{props.children}</dl>
}

/**
 * One label and value of a details page, from the mockups' `details/FieldRow.jsx`: the label in a
 * fixed column on wider screens, stacked above the value on narrow ones. `links` shows one or
 * more linked items instead of `value`, numbered when there are several.
 */
export function FieldRow(props: {
  readonly label: string
  readonly value?: ReactNode
  readonly links?: readonly FieldLink[]
}) {
  const { links } = props
  return (
    <div className="grid grid-cols-1 gap-1 border-b border-divider py-3 last:border-0 sm:grid-cols-[220px_1fr] sm:gap-4">
      <dt className="text-sm text-text-secondary">{props.label}</dt>
      <dd className="min-w-0 break-words text-sm text-text-primary">
        {links === undefined ? (
          props.value
        ) : (
          <ol className={cn('space-y-1', links.length > 1 && 'list-inside list-decimal marker:text-text-light')}>
            {links.map((item, index) => (
              <li key={`${item.label}-${String(index)}`}>
                {item.path !== undefined && item.permission !== undefined ? (
                  <RecordLink to={item.path} permission={item.permission} scope={item.scope}>
                    {item.label}
                  </RecordLink>
                ) : item.path !== undefined ? (
                  <Link to={item.path} className={cn(inlineLink, targetSize)}>
                    {item.label}
                  </Link>
                ) : item.href !== undefined ? (
                  <a href={item.href} className={cn(inlineLink, targetSize)}>
                    {item.label}
                  </a>
                ) : (
                  item.label
                )}
              </li>
            ))}
          </ol>
        )}
      </dd>
    </div>
  )
}
